import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight,
  Heart,
  Target,
  Award,
  Zap,
  Users,
  Building
} from 'lucide-react';

const TEAM_IMAGE = 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_825c5321-5d95-4d75-842e-1e4ffe27dc80/artifacts/nz2oqlak_image.png';

const values = [
  {
    icon: Heart,
    title: 'Passion',
    description: 'Deeply committed to solving manufacturing challenges with technology that makes a real difference.',
    color: '#EF4444',
  },
  {
    icon: Target,
    title: 'Perfection',
    description: 'Attention to detail in every solution we deliver. We don\'t ship until it\'s right.',
    color: '#3B82F6',
  },
  {
    icon: Award,
    title: 'Pride',
    description: 'Taking ownership of our work and its outcomes. Your success is our success.',
    color: '#F59E0B',
  },
  {
    icon: Zap,
    title: 'Promptness',
    description: 'Delivering results swiftly without compromising quality. Time is money in manufacturing.',
    color: '#00D4FF',
  },
];

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const AboutPage = () => {
  return (
    <div className="pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-sm font-medium">
                About Us
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Smarter Workflows.<br />
                <span className="text-[#00D4FF]">Better Growth.</span>
              </h1>
              <p className="text-white/60 text-lg mb-8">
                Cost Genie is an Indian manufacturing software company transforming how 
                auto-component and engineering manufacturers handle RFQ and quotation operations.
              </p>
              <p className="text-white/60">
                We combine deep manufacturing domain expertise with modern technology to 
                deliver solutions that work on the shop floor. Our team understands the 
                challenges of manufacturing because we've lived them.
              </p>
            </motion.div>

            <AnimatedSection>
              <div className="relative">
                <div className="glass-card overflow-hidden">
                  <img 
                    src={TEAM_IMAGE} 
                    alt="Cost Genie Team" 
                    className="w-full aspect-video object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 glass-card p-4">
                  <img src={LOGO_URL} alt="Cost Genie" className="h-8 w-auto" />
                  <p className="text-white/60 text-xs mt-2">CII DigiTech Recognized</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Building, value: '50+', label: 'Manufacturing Companies' },
              { icon: Users, value: '500+', label: 'Active Users' },
              { value: '2019', label: 'Founded' },
              { value: 'India', label: 'Headquartered' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-white/50 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values - The 4Ps */}
      <section className="py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              The 4Ps That Drive Us
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Our core values guide everything we do, from product development to customer support.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <AnimatedSection key={value.title}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="glass-card p-6 text-center h-full"
                >
                  <div 
                    className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${value.color}15` }}
                  >
                    <value.icon className="w-8 h-8" style={{ color: value.color }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-white/60 text-sm">{value.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#00D4FF]/10 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
                <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                  "To empower manufacturing companies with intelligent software that transforms 
                  their quotation operations from spreadsheet chaos to streamlined success, 
                  helping them win more deals faster while maintaining accuracy and control."
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Recognition */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">Recognition</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              Trusted & Recognized
            </h2>
          </AnimatedSection>

          <AnimatedSection>
            <div className="flex flex-wrap justify-center gap-8">
              <div className="glass-card p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00D4FF]/20 flex items-center justify-center">
                  <Award className="w-8 h-8 text-[#00D4FF]" />
                </div>
                <h3 className="font-semibold mb-2">CII DigiTech Recognized</h3>
                <p className="text-white/60 text-sm">Confederation of Indian Industry</p>
              </div>
              <div className="glass-card p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                  <Building className="w-8 h-8 text-[#3B82F6]" />
                </div>
                <h3 className="font-semibold mb-2">50+ Manufacturers</h3>
                <p className="text-white/60 text-sm">Across India</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Work with Us?
              </h2>
              <p className="text-white/60 mb-8 max-w-xl mx-auto">
                Let's discuss how Cost Genie can transform your quotation operations.
              </p>
              <Link
                to="/demo"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all"
                data-testid="about-demo-btn"
              >
                Get in Touch
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section> */}
    </div>
  );
};

export default AboutPage;
